import { useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface AdminChatDetailsProps {
  chat: {
    id: string;
    createdAt: string;
    type: string;
    user?: {
      asfId?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      stripeCustomerId?: string;
      isOldAsfMember?: boolean;
      numImages?: number;
      numChats?: number;
      numSocialPosts?: number;
      hasSettingsInstructions?: boolean;
      settingsInstructions?: string;
    };
    summary?: {
      summaryContent: string;
    };
  };
  showChatId?: boolean;
}

// Collapsible text component
function CollapsibleText({ text }: { text: string }) {
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 150;

  return (
    <div>
      <div className={`${!expanded && isLong ? 'line-clamp-2' : ''}`}>
        <pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{text}</pre>
      </div>
      {isLong && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-blue-600 mt-1 hover:underline">
          {expanded ? tc('show-less') : tc('show-more')}
        </button>
      )}
    </div>
  );
}

export default function AdminChatDetails({ chat, showChatId = true }: AdminChatDetailsProps) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');

  return (
    <div className="space-y-6">
      {/* Chat Details Section */}
      <div className="bg-white border rounded-md shadow-md">
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">{t('chat-details.chat-heading')}</h2>
          <div className="grid grid-cols-1 gap-4 mb-4">
            {showChatId && (
              <div>
                <p className="text-sm text-gray-500">{t('chat-details.label-chat-id')}</p>
                <p className="font-mono text-sm">{chat.id}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-type')}</p>
              <p className="text-sm">{chat.type}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-created')}</p>
              <p className="text-sm">{format(new Date(chat.createdAt), 'MMM d, yyyy h:mm a')}</p>
            </div>
          </div>

          {chat.summary && (
            <div>
              <p className="text-sm text-gray-500 mb-2">{t('chat-details.label-summary')}</p>
              <div className="bg-gray-50 p-4">
                <CollapsibleText text={chat.summary.summaryContent} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Details Section */}
      <div className="bg-white border rounded-md shadow-md">
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">{t('chat-details.user-heading')}</h2>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-user')}</p>
              <p className="text-sm">{chat.user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-user-id')}</p>
              <p className="font-mono text-sm">{chat.user?.asfId}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-stripe-customer')}</p>
              <p className="text-sm">
                {chat.user?.stripeCustomerId ? (
                  <a
                    href={`https://dashboard.stripe.com/customers/${chat.user.stripeCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {chat.user.stripeCustomerId}
                  </a>
                ) : (
                  t('chat-details.not-applicable')
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-asf-member')}</p>
              <p className="text-sm">{chat.user?.isOldAsfMember ? tc('yes') : tc('no')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-num-images')}</p>
              <p className="text-sm">{chat.user?.numImages || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-num-chats')}</p>
              <p className="text-sm">{chat.user?.numChats || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-num-social-posts')}</p>
              <p className="text-sm">{chat.user?.numSocialPosts || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('chat-details.label-has-settings-instructions')}</p>
              <p className="text-sm">{chat.user?.hasSettingsInstructions ? tc('yes') : tc('no')}</p>
            </div>
            {chat.user?.hasSettingsInstructions && (
              <div>
                <p className="text-sm text-gray-500">{t('chat-details.label-settings-instructions')}</p>
                <div className="bg-gray-50 p-4">
                  <CollapsibleText text={chat.user?.settingsInstructions || ''} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
